'use client';

import { Globe2 } from 'lucide-react';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useConvexAuth } from 'convex/react';
import { useRef, useState } from 'react';

import { api } from '@/convex/_generated/api';
import {
  SUPPORTED_LOCALES,
  getNativeLocaleLabel,
  normalizeLocale,
} from '@/i18n/config';
import { getLocaleSelectionPersistence } from '@/i18n/locale-preference';
import { useMutation, useQuery } from '@/lib/convex';
import {
  clearPendingSignupLocale,
  setPendingSignupLocale,
} from '@/lib/auth-locale-preference';
import { cn } from '@/lib/utils';

function withoutLocalePrefix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (
    segments[0] &&
    (SUPPORTED_LOCALES as readonly string[]).includes(segments[0])
  ) {
    return `/${segments.slice(1).join('/')}` || '/';
  }
  return pathname || '/';
}

export function getLocalizedMarketingHref(
  pathname: string,
  targetLocale: string
): string {
  const path = withoutLocalePrefix(pathname);
  if (targetLocale === 'en') return path;
  return `/${targetLocale}${path === '/' ? '' : path}`;
}

export function getLocalizedMarketingDestination({
  pathname,
  targetLocale,
  search,
  hash,
}: {
  pathname: string;
  targetLocale: string;
  search: string;
  hash: string;
}): string {
  return `${getLocalizedMarketingHref(pathname, targetLocale)}${search}${hash}`;
}

export function MarketingLanguageSwitcher({
  variant = 'label',
  tone = 'light',
  className,
}: {
  variant?: 'label' | 'icon';
  tone?: 'light' | 'dark';
  className?: string;
}) {
  const locale = useLocale();
  const pathname = usePathname() ?? '/';
  const currentLocale = normalizeLocale(locale);
  const { isAuthenticated } = useConvexAuth();
  const { data: profile } = useQuery(
    api.users.queries.getProfile,
    isAuthenticated ? {} : 'skip'
  );
  const updateRegionalSettings = useMutation(
    api.users.mutations.updateRegionalSettings
  );
  const localeChangeInFlightRef = useRef(false);
  const [isChangingLocale, setIsChangingLocale] = useState(false);
  const foreground = tone === 'dark' ? 'text-white/80' : 'text-[#8a8279]';

  const handleLocaleChange = async (selectedValue: string) => {
    if (localeChangeInFlightRef.current) return;

    localeChangeInFlightRef.current = true;
    setIsChangingLocale(true);
    const selectedLocale = normalizeLocale(selectedValue);
    const persistence = getLocaleSelectionPersistence({
      isAuthenticated,
      profileTimezone: profile?.settings.timezone,
      browserTimezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    });

    if (persistence.mode === 'profile') {
      try {
        await updateRegionalSettings({
          language: selectedLocale,
          timezone: persistence.timezone,
        });
        clearPendingSignupLocale(selectedLocale);
      } catch (error) {
        localeChangeInFlightRef.current = false;
        setIsChangingLocale(false);
        console.error(
          '[marketing-language-switcher] failed to persist locale',
          error
        );
        return;
      }
    } else {
      setPendingSignupLocale(selectedLocale);
    }

    window.location.href = getLocalizedMarketingDestination({
      pathname,
      targetLocale: selectedLocale,
      search: window.location.search,
      hash: window.location.hash,
    });
  };

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full focus-within:ring-2 focus-within:ring-[#8a8279] focus-within:ring-offset-2 focus-within:outline-none',
        variant === 'icon' ? 'size-11 justify-center' : 'rounded-sm pr-1.5',
        tone === 'dark' && 'focus-within:ring-offset-black',
        tone === 'light' && 'focus-within:ring-offset-white',
        className
      )}
    >
      <select
        value={currentLocale}
        disabled={isChangingLocale}
        aria-label="Select language"
        title="Select language"
        onChange={(event) => void handleLocaleChange(event.target.value)}
        className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
      >
        {SUPPORTED_LOCALES.map((option) => (
          <option key={option} value={option}>
            {getNativeLocaleLabel(option)}
          </option>
        ))}
      </select>
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-flex items-center gap-1.5',
          foreground
        )}
      >
        <Globe2
          className={variant === 'icon' ? 'size-6' : 'size-4'}
          strokeWidth={1.8}
        />
        {variant === 'label' ? (
          <span className="text-[13px] font-medium">
            {getNativeLocaleLabel(currentLocale)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
